import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react'
import { DateRange, StaticDateRangePicker } from '@mui/lab'
import { Box, Divider } from '@mui/material'
import { addDays, differenceInDays, isToday } from 'date-fns'
import { DateRangeQuickFilters } from '../DateRangeQuickFilters'
import { DateQuickFilters, getDateRange, getFormattedCustomRange, parseDateParam } from '../helper'
import { useQuickFilterOptions } from '../hooks'
import { IDateRangeAPI, IDateRangeDropDownProps } from '../types'
import { Popover } from './Popover'

interface ISelectDateRangeProps extends IDateRangeDropDownProps {
  dateRange: DateRange<Date>
  setDateRange: Dispatch<SetStateAction<DateRange<Date>>>
  maxDaysRange?: number
  minDate?: Date
  maxDate?: Date
}

export const SelectDateRange = ({
  initialQuickFilter,
  quickFiltersList,
  dateRange,
  setDateRange,
  maxDaysRange,
  minDate,
  maxDate
}: ISelectDateRangeProps) => {
  const [selectedQuickFilter, setSelectedQuickFilter] =
    useState<DateQuickFilters>(initialQuickFilter)
  const [customQuickFilter, setCustomQuickFilter] = useState('')

  // The Calendar component from MUI is not rerendering when we manually
  // change the endDate due to the maximum days limit, so we are using this state to force a rerender
  const [forceRerender, setForceRerender] = useState(0)
  const forceUpdate = useCallback(() => setForceRerender((prev) => prev + 1), [])

  const dateRangePopoverRef = useRef<IDateRangeAPI | undefined>(undefined)

  const quickFilterOptions = useQuickFilterOptions(quickFiltersList, customQuickFilter)

  const closePopover = () => {
    dateRangePopoverRef.current?.handlePopoverClose()
  }

  const changeDate = (
    selectedQuickFilter: DateQuickFilters,
    dateFilter: { startDate: string; endDate: string }
  ) => {
    const startDate = parseDateParam(dateFilter.startDate)
    const endDate = parseDateParam(dateFilter.endDate)

    setSelectedQuickFilter(selectedQuickFilter)
    if (selectedQuickFilter === DateQuickFilters.DEFINED) {
      setCustomQuickFilter(getFormattedCustomRange([startDate, endDate]) || '')
    }
    setDateRange([startDate, endDate])
  }

  const handleDateRangeChange = ([from, to]: DateRange<Date>) => {
    if (!from || !to) return

    const nextValue = isToday(from) ? DateQuickFilters.TODAY : DateQuickFilters.DEFINED

    let startDate = from
    let endDate = to
    let wasAdjusted = false

    if (maxDaysRange && differenceInDays(endDate, startDate) >= maxDaysRange) {
      endDate = addDays(startDate, maxDaysRange - 1)
      wasAdjusted = true
    }

    const dateFilter = getDateRange(DateQuickFilters.DEFINED, [startDate, endDate])
    changeDate(nextValue, dateFilter)

    if (wasAdjusted) {
      forceUpdate()
    }
  }

  return (
    <Popover
      label={quickFilterOptions[selectedQuickFilter]?.label}
      ref={dateRangePopoverRef}
      title={`Select time range ${maxDaysRange ? `(max ${maxDaysRange} days)` : ''}`}
    >
      <Box display="flex">
        <Box width={230} paddingTop={2.5} paddingLeft={2.5}>
          <DateRangeQuickFilters
            closePopover={closePopover}
            quickFilterOptions={quickFilterOptions}
            setSelectedQuickFilter={setSelectedQuickFilter}
            customQuickFilter={customQuickFilter}
            dateRange={dateRange}
            changeDate={changeDate}
          />
        </Box>
        <Divider orientation="vertical" sx={{ height: 360 }} />
        <Box>
          <StaticDateRangePicker
            key={forceRerender}
            showToolbar={false}
            value={dateRange}
            onChange={handleDateRangeChange}
            renderInput={() => null as any}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Box>
      </Box>
    </Popover>
  )
}
